import { useState } from 'react';
import { CartaSolicitud, MIEMBROS_COMITE } from '../../../mocks/cartas';

interface Props {
  cartas: CartaSolicitud[];
  onMarcarLeida: (id: string) => void;
}

function formatFecha(iso: string): string {
  const d = new Date(iso);
  const meses = ['enero','febrero','marzo','abril','mayo','junio','julio','agosto','septiembre','octubre','noviembre','diciembre'];
  return `${d.getDate()} de ${meses[d.getMonth()]} de ${d.getFullYear()}`;
}

export default function CartasRecibidas({ cartas, onMarcarLeida }: Props) {
  const [cartaSel, setCartaSel] = useState<CartaSolicitud | null>(null);
  const [filtro, setFiltro] = useState<'todas' | 'nuevas' | 'leidas'>('todas');

  const cartasEnviadas = cartas.filter(c => c.estado === 'enviada' || c.estado === 'leida');
  const nuevas = cartasEnviadas.filter(c => c.estado === 'enviada').length;

  const cartasFiltradas = cartasEnviadas.filter(c => {
    if (filtro === 'nuevas') return c.estado === 'enviada';
    if (filtro === 'leidas') return c.estado === 'leida';
    return true;
  });

  const handleVerCarta = (carta: CartaSolicitud) => {
    setCartaSel(carta);
    if (carta.estado === 'enviada') {
      onMarcarLeida(carta.id);
    }
  };

  const handleImprimir = (carta: CartaSolicitud) => {
    const fechaEnvio = carta.fechaEnvio ? formatFecha(carta.fechaEnvio) : formatFecha(new Date().toISOString());
    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Carta de Solicitud – ${carta.proyectoCodigo}</title>
        <style>
          body { font-family: 'Times New Roman', serif; font-size: 12pt; margin: 0; padding: 0; color: #1a1a1a; }
          .page { width: 21cm; min-height: 29.7cm; margin: 0 auto; padding: 2.5cm 3cm; box-sizing: border-box; }
          .header { display: flex; align-items: center; gap: 20px; border-bottom: 2px solid #0d9488; padding-bottom: 16px; margin-bottom: 24px; }
          .header-text h1 { font-size: 14pt; font-weight: bold; margin: 0 0 2px 0; color: #0d9488; }
          .header-text p { font-size: 9pt; margin: 0; color: #555; }
          .fecha-lugar { text-align: right; margin-bottom: 24px; font-size: 11pt; }
          .destinatario { margin-bottom: 20px; font-size: 11pt; }
          .destinatario strong { display: block; }
          .asunto { margin-bottom: 20px; font-size: 11pt; }
          .saludo { margin-bottom: 16px; font-size: 11pt; }
          .cuerpo { font-size: 11pt; line-height: 1.8; text-align: justify; white-space: pre-wrap; margin-bottom: 32px; }
          .firma-area { margin-top: 40px; }
          .firma-linea { border-top: 1px solid #333; width: 220px; margin-bottom: 6px; }
          .firma-nombre { font-size: 11pt; font-weight: bold; }
          .firma-cargo { font-size: 10pt; color: #555; }
          .firma-img { max-height: 60px; margin-bottom: 4px; }
          .footer { margin-top: 40px; border-top: 1px solid #ccc; padding-top: 10px; font-size: 8pt; color: #888; text-align: center; }
          @media print { body { -webkit-print-color-adjust: exact; } }
        </style>
      </head>
      <body>
        <div class="page">
          <div class="header">
            <div class="header-text">
              <h1>Vicerrectorado de Investigación – UGPP</h1>
              <p>Unidad de Gestión de Proyectos y Publicaciones</p>
              <p>Comité de Investigación e Interacción</p>
            </div>
          </div>
          <div class="fecha-lugar">${carta.ciudad || 'Quito'}, ${fechaEnvio}</div>
          <div class="destinatario">
            <strong>Señores</strong>
            Comité de Investigación e Interacción<br>
            Vicerrectorado de Investigación – UGPP<br>
            Presente.-
          </div>
          <div class="asunto"><strong>ASUNTO:</strong> Solicitud de evaluación de proyecto de investigación – ${carta.proyectoCodigo}</div>
          <div class="saludo">Estimados miembros del Comité:</div>
          <div class="cuerpo">${carta.cuerpo}</div>
          <div class="firma-area">
            ${carta.firma?.tipo === 'imagen' ? `<img src="${carta.firma.valor}" class="firma-img" alt="Firma" />` : ''}
            <div class="firma-linea"></div>
            <div class="firma-nombre">${carta.firma?.tipo === 'texto' ? carta.firma.valor : carta.investigadorNombre}</div>
            ${carta.investigadorCargo ? `<div class="firma-cargo">${carta.investigadorCargo}</div>` : ''}
            ${carta.investigadorUnidad ? `<div class="firma-cargo">${carta.investigadorUnidad}</div>` : ''}
            <div class="firma-cargo">${carta.investigadorEmail}</div>
          </div>
          <div class="footer">
            Documento recibido por el Sistema UGPP – Vicerrectorado de Investigación · ${new Date().getFullYear()}
          </div>
        </div>
      </body>
      </html>
    `;
    const win = window.open('', '_blank');
    if (win) {
      win.document.write(html);
      win.document.close();
      win.focus();
      setTimeout(() => { win.print(); }, 500);
    }
  };

  // ── DETALLE DE CARTA ──────────────────────────────────────────────────────
  if (cartaSel) {
    const carta = cartas.find(c => c.id === cartaSel.id) || cartaSel;
    const fechaEnvio = carta.fechaEnvio ? formatFecha(carta.fechaEnvio) : '—';

    return (
      <div>
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => setCartaSel(null)}
            className="whitespace-nowrap flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 cursor-pointer"
          >
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-left-line" /></span>
            Cartas recibidas
          </button>
          <span className="text-slate-300">/</span>
          <h2 className="text-lg font-bold text-slate-800">Carta de {carta.investigadorNombre}</h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Carta principal */}
          <div className="lg:col-span-2">
            {/* Sobre / encabezado */}
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden mb-4">
              <div className="bg-teal-700 px-5 py-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <p className="text-white text-sm font-bold">Vicerrectorado de Investigación – UGPP</p>
                    <p className="text-teal-200 text-xs">Comité de Investigación e Interacción</p>
                  </div>
                  <span className="text-xs font-semibold bg-white/20 text-white px-2.5 py-1 rounded-full border border-white/30">
                    {carta.estado === 'leida' ? 'Leída' : 'Nueva'}
                  </span>
                </div>
              </div>
              <div className="bg-slate-50 px-5 py-3 border-b border-slate-200 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-slate-600">
                <div>
                  <span className="font-semibold text-slate-700">De: </span>
                  {carta.investigadorNombre}
                  {carta.investigadorCargo && ` · ${carta.investigadorCargo}`}
                </div>
                <div>
                  <span className="font-semibold text-slate-700">Correo: </span>
                  <span className="text-teal-600">{carta.investigadorEmail}</span>
                </div>
                {carta.investigadorUnidad && (
                  <div>
                    <span className="font-semibold text-slate-700">Unidad: </span>
                    {carta.investigadorUnidad}
                  </div>
                )}
                <div>
                  <span className="font-semibold text-slate-700">Fecha: </span>
                  {fechaEnvio}
                </div>
                <div className="sm:col-span-2">
                  <span className="font-semibold text-slate-700">Asunto: </span>
                  Solicitud de evaluación – <span className="font-mono font-bold text-teal-700">{carta.proyectoCodigo}</span>
                </div>
              </div>

              {/* Cuerpo */}
              <div className="p-6">
                <p className="text-sm text-slate-700 mb-4">Estimados miembros del Comité:</p>
                <div className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap border-l-2 border-teal-200 pl-4">
                  {carta.cuerpo}
                </div>

                {/* Firma */}
                <div className="mt-8 pt-4 border-t border-slate-100">
                  {carta.firma?.tipo === 'imagen' && (
                    <img src={carta.firma.valor} alt="Firma digital" className="max-h-14 object-contain mb-2" />
                  )}
                  <div className="border-t border-slate-400 w-48 mb-2" />
                  <p className="text-sm font-semibold text-slate-700" style={carta.firma?.tipo === 'texto' ? { fontFamily: 'cursive' } : {}}>
                    {carta.firma?.tipo === 'texto' ? carta.firma.valor : carta.investigadorNombre}
                  </p>
                  {carta.investigadorCargo && <p className="text-xs text-slate-500">{carta.investigadorCargo}</p>}
                  {carta.investigadorUnidad && <p className="text-xs text-slate-500">{carta.investigadorUnidad}</p>}
                  <p className="text-xs text-teal-600">{carta.investigadorEmail}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Panel derecho */}
          <div className="space-y-4">
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-slate-700 mb-3">Acciones</h3>
              <div className="space-y-2">
                <button
                  onClick={() => handleImprimir(carta)}
                  className="whitespace-nowrap w-full flex items-center justify-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors cursor-pointer"
                >
                  <span className="w-4 h-4 flex items-center justify-center"><i className="ri-printer-line" /></span>
                  Imprimir / Descargar PDF
                </button>
                <button
                  onClick={() => setCartaSel(null)}
                  className="whitespace-nowrap w-full flex items-center justify-center gap-2 border border-slate-300 text-slate-700 hover:bg-slate-50 px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors cursor-pointer"
                >
                  <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-left-line" /></span>
                  Volver a la lista
                </button>
              </div>
            </div>

            {/* Info del proyecto */}
            <div className="bg-teal-50 border border-teal-200 rounded-xl p-4">
              <p className="text-xs font-semibold text-teal-800 mb-2 flex items-center gap-1.5">
                <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-folder-line" /></span>
                Proyecto referenciado
              </p>
              <p className="font-mono text-xs font-bold text-teal-700 mb-1">{carta.proyectoCodigo}</p>
              <p className="text-xs text-teal-800 font-medium leading-snug">{carta.proyectoTitulo}</p>
            </div>

            {/* Notificaciones enviadas */}
            {carta.notificadoComite && (
              <div className="bg-white border border-slate-200 rounded-xl p-4">
                <p className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1.5">
                  <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-mail-check-line text-teal-600" /></span>
                  Notificaciones enviadas
                </p>
                <div className="space-y-1.5">
                  {MIEMBROS_COMITE.map((m, i) => (
                    <div key={i} className="flex items-center gap-1.5 text-xs text-slate-600">
                      <span className="w-3 h-3 flex items-center justify-center text-teal-600"><i className="ri-check-line" /></span>
                      <span>{m.nombre}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── LISTA DE CARTAS ───────────────────────────────────────────────────────
  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <span className="w-7 h-7 flex items-center justify-center bg-teal-100 rounded-lg">
              <i className="ri-inbox-line text-teal-600 text-base" />
            </span>
            Cartas de Solicitud Recibidas
            {nuevas > 0 && (
              <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                {nuevas} nueva{nuevas > 1 ? 's' : ''}
              </span>
            )}
          </h2>
          <p className="text-slate-500 text-sm mt-1">
            Cartas formales de solicitud enviadas por los investigadores al Comité.
          </p>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex gap-2 mb-5">
        {(['todas', 'nuevas', 'leidas'] as const).map(f => (
          <button
            key={f}
            onClick={() => setFiltro(f)}
            className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors cursor-pointer ${
              filtro === f ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-slate-600 border-slate-200 hover:border-teal-300'
            }`}
          >
            {f === 'todas' ? `Todas (${cartasEnviadas.length})` : f === 'nuevas' ? `Nuevas (${nuevas})` : `Leídas (${cartasEnviadas.length - nuevas})`}
          </button>
        ))}
      </div>

      {/* Miembros del comité */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-5">
        <p className="text-xs font-semibold text-slate-600 mb-2 flex items-center gap-1.5">
          <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-team-line" /></span>
          Miembros notificados automáticamente al recibir cada carta
        </p>
        <div className="flex flex-wrap gap-2">
          {MIEMBROS_COMITE.map((m, i) => (
            <div key={i} className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-full px-3 py-1 text-xs text-slate-600">
              <span className="w-3 h-3 flex items-center justify-center"><i className="ri-user-line text-teal-600" /></span>
              <span className="font-medium">{m.nombre}</span>
              <span className="text-slate-400">·</span>
              <span className="text-teal-600">{m.email}</span>
            </div>
          ))}
        </div>
      </div>

      {cartasFiltradas.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="w-16 h-16 flex items-center justify-center bg-slate-100 rounded-full mb-4">
            <i className="ri-inbox-line text-3xl text-slate-400" />
          </div>
          <h3 className="text-base font-semibold text-slate-700 mb-2">
            {filtro === 'nuevas' ? 'No hay cartas nuevas' : filtro === 'leidas' ? 'No hay cartas leídas' : 'No se han recibido cartas aún'}
          </h3>
          <p className="text-slate-500 text-sm max-w-sm">
            {filtro === 'todas'
              ? 'Cuando los investigadores envíen sus cartas de solicitud, aparecerán aquí.'
              : 'Cambie el filtro para ver otras cartas.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {cartasFiltradas.map(carta => (
            <div
              key={carta.id}
              className={`bg-white border rounded-xl overflow-hidden transition-all ${
                carta.estado === 'enviada' ? 'border-teal-300 ring-1 ring-teal-200' : 'border-slate-200'
              }`}
            >
              <div className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 flex items-center justify-center rounded-lg flex-shrink-0 ${
                    carta.estado === 'enviada' ? 'bg-teal-100' : 'bg-slate-100'
                  }`}>
                    <i className={`text-lg ${
                      carta.estado === 'enviada' ? 'ri-mail-unread-line text-teal-600' : 'ri-mail-check-line text-slate-500'
                    }`} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="font-mono text-xs font-bold text-teal-700 bg-teal-50 border border-teal-200 px-2 py-0.5 rounded">
                        {carta.proyectoCodigo}
                      </span>
                      {carta.estado === 'enviada' && (
                        <span className="text-xs font-bold text-white bg-red-500 px-2 py-0.5 rounded-full">
                          NUEVA
                        </span>
                      )}
                    </div>
                    <p className="text-sm font-semibold text-slate-800">{carta.investigadorNombre}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{carta.proyectoTitulo}</p>
                    {carta.investigadorCargo && (
                      <p className="text-xs text-slate-400">{carta.investigadorCargo}{carta.investigadorUnidad ? ` · ${carta.investigadorUnidad}` : ''}</p>
                    )}
                    {carta.fechaEnvio && (
                      <p className="text-xs text-slate-400 mt-0.5">
                        Recibida el {new Date(carta.fechaEnvio).toLocaleDateString('es-EC', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <button
                    onClick={() => handleImprimir(carta)}
                    className="whitespace-nowrap flex items-center gap-1.5 text-xs font-semibold text-slate-600 border border-slate-200 bg-slate-50 hover:bg-slate-100 px-3 py-1.5 rounded-md transition-colors cursor-pointer"
                  >
                    <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-printer-line" /></span>
                    Imprimir
                  </button>
                  <button
                    onClick={() => handleVerCarta(carta)}
                    className="whitespace-nowrap flex items-center gap-1.5 text-xs font-semibold text-teal-600 border border-teal-300 bg-teal-50 hover:bg-teal-100 px-3 py-1.5 rounded-md transition-colors cursor-pointer"
                  >
                    <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-eye-line" /></span>
                    Ver carta
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
